import { FeatureGroup, useMap } from "react-leaflet";
import {
  leafletLayer,
  type PaintRule,
  PolygonSymbolizer,
} from "protomaps-leaflet";
import { useCallback, useEffect, useRef, useState } from "react";
import { type GridLayer, type LayersControlEvent } from "leaflet";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { useProtomapsPrintAdapter } from "@/features/map/hooks/useProtomapsPrintAdapter";
import type { PrintRenderContext } from "@/features/map/utils/printRenderScale";

interface Props {
  defaultVisible: boolean;
  visible?: boolean;
}

// UIに表示するレイヤー名
export const CITY_PLANNING_AREA_LAYER_NAME = "都市計画区域";

// pmtilesが内部で保持しているレイヤー名
const CITY_PLANNING_AREA_DATA_LAYER_NAME = "mlit_toshi_tosiko_tk_000087_tokei";

const host = process.env.VITE_COMMON_TILE_HOST ?? "";
const url = `${host}/city-planning-tiles/city_planning_tokei_87.pmtiles`;

export const CITY_PLANNING_AREA_MIN_ZOOM = 12;
export const CITY_PLANNING_AREA_MAX_ZOOM = 14;
const MIN_ZOOM = CITY_PLANNING_AREA_MIN_ZOOM;
const MAX_ZOOM = CITY_PLANNING_AREA_MAX_ZOOM;
const MAX_DATA_ZOOM = 14;

const FILL_COLOR = "rgba(227, 227, 227, 0.5)";
const STROKE_COLOR = "black";
const STROKE_WIDTH = 0.5;

const attribution =
  '<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>';

export const buildCityPlanningAreaPaintRules = (
  renderScale: number,
  zoomOffset: number
): PaintRule[] => [
  {
    dataLayer: CITY_PLANNING_AREA_DATA_LAYER_NAME,
    symbolizer: new PolygonSymbolizer({
      fill: FILL_COLOR,
      opacity: 1,
      stroke: STROKE_COLOR,
      width: STROKE_WIDTH * renderScale,
    }),
    minzoom: MIN_ZOOM + zoomOffset,
    maxzoom: MAX_ZOOM + zoomOffset,
  },
];

/**
 * 都市計画区域（tk_000087 tokei）のレイヤー
 */
export const CityPlanningAreaLayer: React.FC<Props> = ({
  defaultVisible,
  visible: visibleProp,
}) => {
  const map = useMap();
  const [visible, setVisible] = useState(defaultVisible);
  const layerRef = useRef<GridLayer | null>(null);
  const createPrintLayer = useCallback(
    (context: PrintRenderContext): GridLayer =>
      leafletLayer({
        url,
        paintRules: buildCityPlanningAreaPaintRules(
          context.printScale,
          context.zoomOffset
        ),
        maxDataZoom: MAX_DATA_ZOOM,
        attribution,
      }) as GridLayer,
    []
  );

  useProtomapsPrintAdapter({
    map,
    enabled: visible,
    normalLayerRef: layerRef,
    createPrintLayer,
  });

  useEffect(() => {
    if (!map) return;

    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
      return;
    }

    const handleOverlayAdd = (e: LayersControlEvent): void => {
      if (e.name === CITY_PLANNING_AREA_LAYER_NAME) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: LayersControlEvent): void => {
      if (e.name === CITY_PLANNING_AREA_LAYER_NAME) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map, visibleProp]);

  useEffect(() => {
    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
    }
  }, [visibleProp]);

  useEffect(() => {
    if (!map) return;

    const layer = leafletLayer({
      url,
      paintRules: buildCityPlanningAreaPaintRules(1, 0),
      maxDataZoom: MAX_DATA_ZOOM,
      attribution,
    }) as GridLayer;

    layer.setZIndex(LAYER_Z_INDEX.CITY_PLANNING_AREA);
    layerRef.current = layer;

    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !layerRef.current) return;

    if (visible) {
      layerRef.current.addTo(map);
    } else {
      map.removeLayer(layerRef.current);
    }
  }, [map, visible]);

  return <FeatureGroup />;
};
